import React, { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { Error, InputWrap, LabelWrapper } from "./styled";
import { Box, Button, Label } from "theme-ui";
import { useGoogleMapsLoader } from "../../../hooks/googleMapsLoader";

const mapContainerStyle = { width: "100%", height: "400px" };
const defaultCenter = { lat: 39.8283, lng: -98.5795 };

type DrawingMode = "polygon" | "circle" | null;

export interface DrawGoogleMapContextPolygon {
  id: string;
  name?: string | null;
  geometry?: any; // GeoJSON polygon
}

export default function DrawGoogleMap(props: any) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  if (loadError) return <p>Error loading maps</p>;
  return !isLoaded ? <p>Loading maps...</p> : <TheMap {...props} />;
}

const TheMap = ({
  label,
  value,
  description,
  required,
  disabled,
  $customStyles,
  $responseErrors,
  $errors,
  onChange,
  // Session 30 — optional context polygons rendered behind the editable
  // shape. Used by the Service Area create/edit modals to show existing
  // areas as muted outlines for reference while drawing.
  contextPolygons,
  excludeId,
}: {
  label?: string | React.ReactNode;
  value?: { type: string; coordinates: [[number, number]] };
  description?: string;
  required?: boolean;
  disabled?: boolean;
  $customStyles?: any;
  $responseErrors?: any;
  $errors?: any;
  onChange?: (e?: any) => void;
  contextPolygons?: DrawGoogleMapContextPolygon[];
  excludeId?: string | null;
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [selectedShape, setSelectedShape] = useState<google.maps.Polygon | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Drawing state
  const [drawingMode, setDrawingMode] = useState<DrawingMode>(null);
  const drawingModeRef = useRef<DrawingMode>(null);
  const tempMarkersRef = useRef<google.maps.Marker[]>([]);
  const tempPolylineRef = useRef<google.maps.Polyline | null>(null);
  const tempPointsRef = useRef<google.maps.LatLng[]>([]);
  const circleCenterRef = useRef<google.maps.LatLng | null>(null);
  const tempCircleRef = useRef<google.maps.Circle | null>(null);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dblClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const mouseMoveListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Session 30 — refs holding rendered context polygon overlays so we can
  // clear + rebuild them when the list or excludeId changes without
  // touching drawing state.
  const contextPolygonsRef = useRef<google.maps.Polygon[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    drawingModeRef.current = drawingMode;
  }, [drawingMode]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  const getPolygonCenter = (polygon: google.maps.Polygon): google.maps.LatLng => {
    const path = polygon.getPath();
    let sumLat = 0,
      sumLng = 0;
    const numPoints = path.getLength();
    for (let i = 0; i < numPoints; i++) {
      const point = path.getAt(i);
      sumLat += point.lat();
      sumLng += point.lng();
    }
    return new google.maps.LatLng(sumLat / numPoints, sumLng / numPoints);
  };

  const updateSelectionValue = useCallback(
    (shape: google.maps.Polygon) => {
      const obj = {
        type: "Polygon",
        coordinates: [shape.getPath().getArray().map((latlng) => [latlng.lng(), latlng.lat()])],
      };
      if (typeof onChange === "function") {
        onChange(obj);
      }
    },
    [onChange]
  );

  const listenerSelection = useCallback(
    (shape: google.maps.Polygon) => {
      ["set_at", "insert_at"].forEach((eventName) => {
        shape.getPath().addListener(eventName, () => {
          updateSelectionValue(shape);
        });
      });
      ["dragstart", "drag", "dragend"].forEach((eventName) => {
        shape.addListener(eventName, () => {
          updateSelectionValue(shape);
        });
      });
    },
    [updateSelectionValue]
  );

  const clearTempDrawing = useCallback(() => {
    tempMarkersRef.current.forEach((m) => m.setMap(null));
    tempMarkersRef.current = [];
    tempPolylineRef.current?.setMap(null);
    tempPolylineRef.current = null;
    tempCircleRef.current?.setMap(null);
    tempCircleRef.current = null;
    tempPointsRef.current = [];
    circleCenterRef.current = null;
  }, []);

  const cleanupListeners = useCallback(() => {
    clickListenerRef.current?.remove();
    clickListenerRef.current = null;
    dblClickListenerRef.current?.remove();
    dblClickListenerRef.current = null;
    mouseMoveListenerRef.current?.remove();
    mouseMoveListenerRef.current = null;
  }, []);

  const deleteSelectedShape = useCallback(() => {
    selectedShape?.setMap(null);
    setSelectedShape(null);
    if (typeof onChange === "function") {
      onChange(undefined);
    }
  }, [selectedShape, onChange]);

  const finishPolygon = useCallback(
    (points: google.maps.LatLng[]) => {
      if (!mapRef.current || points.length < 3) return;
      clearTempDrawing();

      // Remove existing shape
      selectedShape?.setMap(null);

      const polygon = new google.maps.Polygon({
        paths: points,
        strokeWeight: 0,
        fillOpacity: 0.45,
        editable: true,
        draggable: true,
      });
      polygon.setMap(mapRef.current);
      setSelectedShape(polygon);
      listenerSelection(polygon);
      updateSelectionValue(polygon);
      setDrawingMode(null);
    },
    [clearTempDrawing, selectedShape, listenerSelection, updateSelectionValue]
  );

  const circlToPolygon = useCallback(
    (center: google.maps.LatLng, radius: number) => {
      const basePoints = 5;
      const maxPoints = 60;
      const scalingFactor = 100;
      const points = Math.min(Math.max(basePoints, Math.floor(Math.log(radius / scalingFactor) * 5)), maxPoints);
      const a: google.maps.LatLng[] = [];
      const p = 360 / points;
      let d = 0;
      for (let i = 0; i < points; ++i, d += p) {
        a.push(google.maps.geometry.spherical.computeOffset(center, radius, d));
      }
      return a;
    },
    []
  );

  const finishCircle = useCallback(
    (center: google.maps.LatLng, radius: number) => {
      if (!mapRef.current || radius < 10) return;
      clearTempDrawing();

      selectedShape?.setMap(null);

      const path = circlToPolygon(center, radius);
      const polygon = new google.maps.Polygon({
        paths: path,
        strokeWeight: 0,
        fillOpacity: 0.45,
        editable: true,
        draggable: true,
      });
      polygon.setMap(mapRef.current);
      setSelectedShape(polygon);
      listenerSelection(polygon);
      updateSelectionValue(polygon);
      setDrawingMode(null);
    },
    [clearTempDrawing, selectedShape, circlToPolygon, listenerSelection, updateSelectionValue]
  );

  // Set up drawing listeners when mode changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    cleanupListeners();
    clearTempDrawing();

    if (!drawingMode || disabled) {
      map.setOptions({ draggableCursor: undefined });
      return;
    }

    map.setOptions({ draggableCursor: "crosshair" });

    if (drawingMode === "polygon") {
      clickListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || drawingModeRef.current !== "polygon") return;
        const points = tempPointsRef.current;

        // Check if clicking near first point to close
        if (points.length >= 3) {
          const first = points[0];
          const proj = map.getProjection();
          if (proj) {
            const firstPx = proj.fromLatLngToPoint(first);
            const clickPx = proj.fromLatLngToPoint(e.latLng);
            if (firstPx && clickPx) {
              const zoom = map.getZoom() || 10;
              const scale = Math.pow(2, zoom);
              const dx = (firstPx.x - clickPx.x) * scale;
              const dy = (firstPx.y - clickPx.y) * scale;
              if (Math.sqrt(dx * dx + dy * dy) < 15) {
                finishPolygon(points);
                return;
              }
            }
          }
        }

        points.push(e.latLng);

        // Add vertex marker
        const marker = new google.maps.Marker({
          position: e.latLng,
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: points.length === 1 ? 7 : 5,
            fillColor: points.length === 1 ? "#4285F4" : "#ffffff",
            fillOpacity: 1,
            strokeColor: "#4285F4",
            strokeWeight: 2,
          },
          clickable: points.length === 1,
        });

        if (points.length === 1) {
          marker.addListener("click", () => {
            if (tempPointsRef.current.length >= 3) {
              finishPolygon(tempPointsRef.current);
            }
          });
        }

        tempMarkersRef.current.push(marker);

        // Update polyline
        tempPolylineRef.current?.setMap(null);
        tempPolylineRef.current = new google.maps.Polyline({
          path: points,
          strokeColor: "#4285F4",
          strokeWeight: 2,
          map,
        });
      });

      dblClickListenerRef.current = map.addListener("dblclick", (e: google.maps.MapMouseEvent) => {
        if (drawingModeRef.current !== "polygon") return;
        e.stop?.();
        const points = tempPointsRef.current;
        if (points.length >= 3) {
          finishPolygon(points);
        }
      });
    }

    if (drawingMode === "circle") {
      clickListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || drawingModeRef.current !== "circle") return;

        if (!circleCenterRef.current) {
          // First click - set center
          circleCenterRef.current = e.latLng;
          const marker = new google.maps.Marker({
            position: e.latLng,
            map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#4285F4",
              strokeWeight: 2,
            },
          });
          tempMarkersRef.current.push(marker);

          // Show preview circle on mousemove
          mouseMoveListenerRef.current = map.addListener("mousemove", (me: google.maps.MapMouseEvent) => {
            if (!me.latLng || !circleCenterRef.current) return;
            const radius = google.maps.geometry.spherical.computeDistanceBetween(circleCenterRef.current, me.latLng);
            if (tempCircleRef.current) {
              tempCircleRef.current.setRadius(radius);
            } else {
              tempCircleRef.current = new google.maps.Circle({
                center: circleCenterRef.current,
                radius,
                strokeColor: "#4285F4",
                strokeWeight: 2,
                fillColor: "#4285F4",
                fillOpacity: 0.15,
                map,
              });
            }
          });
        } else {
          // Second click - finish circle
          const radius = google.maps.geometry.spherical.computeDistanceBetween(circleCenterRef.current, e.latLng);
          finishCircle(circleCenterRef.current, radius);
        }
      });
    }

    return () => {
      cleanupListeners();
    };
  }, [drawingMode, mapLoaded, disabled, cleanupListeners, clearTempDrawing, finishPolygon, finishCircle]);

  // Cancel drawing on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && drawingMode) {
        clearTempDrawing();
        cleanupListeners();
        setDrawingMode(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawingMode, clearTempDrawing, cleanupListeners]);

  const [borderError, setBorderError] = useState(false);
  useEffect(() => {
    setBorderError(!!($responseErrors || $errors));
  }, [$responseErrors, $errors]);

  // Render existing value
  useEffect(() => {
    if (value && value.coordinates && value.type === "Polygon" && mapLoaded && mapRef.current) {
      selectedShape?.setMap(null);

      const newPath = value.coordinates[0].map(([lng, lat]: any) => new google.maps.LatLng(lat, lng));
      const polygon = new google.maps.Polygon({
        paths: newPath,
        strokeWeight: 0,
        fillOpacity: 0.45,
        editable: !disabled,
        draggable: !disabled,
      });

      polygon.setMap(mapRef.current);
      const center = getPolygonCenter(polygon);
      setMapCenter({ lat: center.lat(), lng: center.lng() });
      setSelectedShape(polygon);
      listenerSelection(polygon);
    }
  }, [value, disabled, mapLoaded]);

  // Session 30 — render context polygons whenever the list or excludeId
  // changes. Rebuilds from scratch each time (list is small: <=194 items,
  // typically far fewer within the viewport). Runs independently of
  // drawing state so it never interferes with in-progress drawings.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    // Clear previously rendered context polygons.
    contextPolygonsRef.current.forEach((p) => p.setMap(null));
    contextPolygonsRef.current = [];

    if (!Array.isArray(contextPolygons) || contextPolygons.length === 0) return;

    contextPolygons.forEach((area) => {
      if (excludeId && area.id === excludeId) return;
      const geom = area.geometry;
      if (!geom || geom.type !== "Polygon" || !Array.isArray(geom.coordinates) || !geom.coordinates[0]) return;

      const path = geom.coordinates[0]
        .filter((pt: any) => Array.isArray(pt) && pt.length >= 2)
        .map(([lng, lat]: [number, number]) => new google.maps.LatLng(lat, lng));
      if (path.length < 3) return;

      const poly = new google.maps.Polygon({
        paths: path,
        strokeColor: "#818181",
        strokeOpacity: 0.6,
        strokeWeight: 1,
        fillColor: "#818181",
        fillOpacity: 0.08,
        clickable: false,
        zIndex: 0,
      });
      poly.setMap(map);
      contextPolygonsRef.current.push(poly);
    });

    return () => {
      // On unmount / next run, clear overlays.
      contextPolygonsRef.current.forEach((p) => p.setMap(null));
      contextPolygonsRef.current = [];
    };
  }, [contextPolygons, excludeId, mapLoaded]);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(({ coords }) =>
      setMapCenter({ lat: coords.latitude, lng: coords.longitude })
    );
  }, []);

  const toolbarStyle: React.CSSProperties = {
    position: "absolute",
    top: 10,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: 4,
    zIndex: 1,
    background: "#fff",
    borderRadius: 4,
    boxShadow: "0 2px 6px rgba(0,0,0,.3)",
    padding: 4,
  };

  const toolBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 12px",
    fontSize: 12,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    border: "1px solid #ccc",
    borderRadius: 3,
    cursor: "pointer",
    background: active ? "#4285F4" : "#fff",
    color: active ? "#fff" : "#333",
  });

  return (
    <InputWrap sx={$customStyles} $errors={borderError}>
      {label && (
        <LabelWrapper>
          <Label>{label}</Label>
          {required ? <span>*</span> : ""}
        </LabelWrapper>
      )}
      <div style={{ position: "relative" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={5}
          center={mapCenter}
          onLoad={onLoad}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            disableDoubleClickZoom: drawingMode === "polygon",
          }}
        />
        {!disabled && (
          <div style={toolbarStyle}>
            <button
              type="button"
              style={toolBtnStyle(drawingMode === "polygon")}
              onClick={() => {
                clearTempDrawing();
                setDrawingMode(drawingMode === "polygon" ? null : "polygon");
              }}
              title="Draw polygon — click to add points, click first point or double-click to finish"
            >
              ⬠ Polygon
            </button>
            <button
              type="button"
              style={toolBtnStyle(drawingMode === "circle")}
              onClick={() => {
                clearTempDrawing();
                setDrawingMode(drawingMode === "circle" ? null : "circle");
              }}
              title="Draw circle — click for center, click again to set radius"
            >
              ⬤ Circle
            </button>
          </div>
        )}
      </div>
      {!disabled && (
        <Box>
          <Button
            type="button"
            variant="secondaryOutline"
            onClick={() => {
              clearTempDrawing();
              cleanupListeners();
              setDrawingMode(null);
              deleteSelectedShape();
            }}
            disabled={disabled}
            style={{ marginTop: 20 }}
          >
            Clear
          </Button>
        </Box>
      )}
      {description && (
        <p>
          <small>{description}</small>
        </p>
      )}
      {$errors && <Error>{$errors}</Error>}
    </InputWrap>
  );
};
